/**
 * Zambia Independence Day (24 October) login theme.
 *
 * Dates are evaluated on the Africa/Lusaka calendar (UTC+02:00, no daylight
 * saving), so the theme switches at Lusaka midnight wherever the user is.
 */

export const ZAMBIA_TIME_ZONE = 'Africa/Lusaka'
const LUSAKA_OFFSET_MS = 2 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000

export type LoginTheme = {
  pageStyle: { background: string }
  logoRing: string
  heading: string
  inputFocus: string
  buttonStyle: { background: string }
  footer: string
}

/** The original login colours — restored exactly outside 24 October. */
export const defaultLoginTheme: LoginTheme = {
  pageStyle: {
    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #f43f5e 100%)',
  },
  logoRing: 'border-purple-500',
  heading: 'text-pink-600',
  inputFocus: 'focus:ring-pink-500 focus:border-pink-500',
  buttonStyle: { background: 'linear-gradient(90deg, #ec4899 0%, #f43f5e 100%)' },
  footer: 'text-white opacity-80',
}

/** Zambian flag colours: green dominant, black, red and orange accents. */
export const zambiaFlagColors = {
  green: '#198A00',
  greenDark: '#136B00',
  black: '#111111',
  red: '#DE2010',
  orange: '#EF7D00',
}

export const independenceDayLoginTheme: LoginTheme = {
  pageStyle: { background: zambiaFlagColors.green },
  logoRing: 'border-[#198A00]',
  heading: 'text-[#136B00]',
  inputFocus: 'focus:ring-[#198A00] focus:border-[#198A00]',
  buttonStyle: { background: zambiaFlagColors.green },
  footer: 'text-white',
}

/** Month and day on the Lusaka calendar for a given instant. */
function lusakaMonthDay(now: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ZAMBIA_TIME_ZONE,
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(now)
  const read = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value)
  return { month: read('month'), day: read('day') }
}

/** True from 24 Oct 00:00 until 25 Oct 00:00, Lusaka time, every year. */
export function isZambiaIndependenceDay(now: Date = new Date()): boolean {
  const { month, day } = lusakaMonthDay(now)
  return month === 10 && day === 24
}

/** Milliseconds until the next Lusaka midnight (when the theme may change). */
export function msUntilNextLusakaMidnight(now: Date = new Date()): number {
  const intoDay = (now.getTime() + LUSAKA_OFFSET_MS) % DAY_MS
  return DAY_MS - intoDay
}
